import { useCallback, useEffect, useState } from 'react';
import { usersApi, getStoredUser, type UserBrief, type HandoverRecord, type HandoverPreview, type HandoverReview, type PendingReviews, type PendingReviewDetailItem, type ReviewMetrics } from '../api/client.js';
import { ErrorBanner, SkeletonRows, TableEmpty } from '../components/States.js';

/**
 * 8.4 团队 / 成员管理（仅 admin）：查看成员、新建成员、停用/启用、变更角色、重置密码。
 * 停用为软删（is_active=0），停用后不可登录、且不再是合法「负责人」；不允许停用自己/变更自己（服务端同校验）。
 * 8.4.3 停用交接：名下尚有启用档案时服务端 409，前端弹交接人选择（转入成员或转公开）后重试。
 */
const ROLE_LABELS: Record<string, string> = {
  admin: '管理员',
  senior_accountant: '主管会计',
  accountant: '会计',
  viewer: '浏览员',
};

/** 8.4.5 归属变更类型（审计面板展示） */
const REASON_LABELS: Record<string, { text: string; cls: string }> = {
  deactivation: { text: '停用交接', cls: 'bg-amber-100 text-amber-700' },
  manual: { text: '直接重指派', cls: 'bg-sky-100 text-sky-700' },
};

/** 8.4.9 交接审计复核结论（审计面板展示 + 批注弹框） */
const REVIEW_LABELS: Record<string, { text: string; cls: string }> = {
  confirmed: { text: '已确认', cls: 'bg-emerald-100 text-emerald-700' },
  corrected: { text: '已更正', cls: 'bg-sky-100 text-sky-700' },
  flagged: { text: '可疑待核', cls: 'bg-rose-100 text-rose-700' },
};

/** 8.4.8 离职向导：往来单位类型中文（与档案页口径一致） */
const PARTY_TYPE_LABELS: Record<string, string> = { customer: '客户', supplier: '供应商', both: '客户+供应商' };
const money = (n: number) => n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyForm = { username: '', password: '', displayName: '', role: 'accountant' };

export default function Members() {
  const me = getStoredUser();
  const isAdmin = me?.role === 'admin';
  const [users, setUsers] = useState<UserBrief[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  // 8.4.3 停用交接弹窗：409 后打开，选定 transferTo（'public' 或成员 id）重试
  const [handover, setHandover] = useState<{ user: UserBrief; count: number } | null>(null);
  const [transferTo, setTransferTo] = useState('');
  const [handoverSaving, setHandoverSaving] = useState(false);
  // 8.4.4 交接审计轨迹：面板数据 + 按成员/时间范围过滤 + 8.4.7 分页（加载更多）
  const PAGE = 20;
  const [handovers, setHandovers] = useState<HandoverRecord[]>([]);
  const [handoverFilter, setHandoverFilter] = useState('');
  const [handoverFrom, setHandoverFrom] = useState('');
  const [handoverTo, setHandoverTo] = useState('');
  const [handoverOffset, setHandoverOffset] = useState(0);
  const [handoverHasMore, setHandoverHasMore] = useState(false);
  const [handoversLoading, setHandoversLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  // 8.4.8 离职交接向导：预览 + 选定接管人 + 提交
  const [wizard, setWizard] = useState<{ user: UserBrief; preview: HandoverPreview | null } | null>(null);
  const [wizLoading, setWizLoading] = useState(false);
  const [wizTo, setWizTo] = useState('');
  const [wizSaving, setWizSaving] = useState(false);
  // 8.4.14 离职预警下钻：懒加载该成员经手 flagged 未销案明细（null=未展开）
  const [wizDetail, setWizDetail] = useState<PendingReviewDetailItem[] | null>(null);
  const [wizDetailLoading, setWizDetailLoading] = useState(false);
  // 8.4.15 下钻清单行内直接销案（复用 resolveHandover）：wizResolvingId = 当前展开确认框的行
  const [wizResolvingId, setWizResolvingId] = useState<string | null>(null);
  const [wizResolveSaving, setWizResolveSaving] = useState(false);
  // 8.4.16 行内销案附注（可选）：留空则后端回退默认文案，填了则作为 resolveHandover 的 note
  const [wizResolveNote, setWizResolveNote] = useState('');
  // 8.4.9 交接审计复核批注：选中行 + verdict/note + 提交
  const [reviewing, setReviewing] = useState<HandoverRecord | null>(null);
  const [reviewVerdict, setReviewVerdict] = useState('confirmed');
  const [reviewNote, setReviewNote] = useState('');
  const [reviewSaving, setReviewSaving] = useState(false);
  // 8.4.10 复核轨迹全史：选中行 + 历次批注（懒加载）
  const [trail, setTrail] = useState<{ handover: HandoverRecord; reviews: HandoverReview[] } | null>(null);
  const [trailLoading, setTrailLoading] = useState(false);
  // 8.4.11 复核状态筛选 + 销案（仅 flagged 可行）
  const [reviewFilter, setReviewFilter] = useState('');
  const [resolving, setResolving] = useState<HandoverRecord | null>(null);
  const [resolveNote, setResolveNote] = useState('');
  const [resolveSaving, setResolveSaving] = useState(false);
  // 8.4.12 待处理汇总：pending/overdue 横幅与行内超期标记共用同一 days 阈值
  const [pending, setPending] = useState<PendingReviews | null>(null);
  // 8.4.18 复核时效轻报表
  const [metrics, setMetrics] = useState<ReviewMetrics | null>(null);

  // append=false 替换（首次/改条件）；append=true 追加（加载更多）
  const fetchHandovers = useCallback(async (offset: number, append: boolean) => {
    setHandoversLoading(true);
    try {
      const rows = await usersApi.listHandovers({
        userId: handoverFilter || undefined,
        from: handoverFrom || undefined,
        to: handoverTo || undefined,
        review: reviewFilter || undefined,
        limit: PAGE, offset: offset || undefined,
      });
      setHandovers((prev) => (append ? [...prev, ...rows] : rows));
      setHandoverOffset(offset + rows.length);
      setHandoverHasMore(rows.length === PAGE);
    } catch {
      // 审计面板加载失败不阻断主流程，静默降级
      if (!append) { setHandovers([]); setHandoverOffset(0); }
      setHandoverHasMore(false);
    } finally {
      setHandoversLoading(false);
    }
  }, [handoverFilter, handoverFrom, handoverTo, reviewFilter]);

  const doExport = useCallback(async () => {
    setExporting(true);
    try {
      await usersApi.exportHandovers({
        userId: handoverFilter || undefined,
        from: handoverFrom || undefined,
        to: handoverTo || undefined,
        review: reviewFilter || undefined,
      });
    } catch (e: any) {
      setError(e?.message || '导出失败');
    } finally {
      setExporting(false);
    }
  }, [handoverFilter, handoverFrom, handoverTo, reviewFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setUsers(await usersApi.list());
    } catch (e: any) {
      setError(e.message || '加载成员失败');
    } finally {
      setLoading(false);
    }
  }, []);

  // 8.4.12 拉待处理汇总（失败静默：属被动预警不阻断主流程）
  const fetchPending = useCallback(async () => {
    try {
      setPending(await usersApi.pendingReviews());
    } catch {
      /* 静默 */
    }
  }, []);

  // 8.4.18 拉复核时效轻报表（失败静默）
  const fetchMetrics = useCallback(async () => {
    try {
      setMetrics(await usersApi.reviewMetrics());
    } catch {
      /* 静默 */
    }
  }, []);

  // 行内超期判定：最新 flagged 批注距今天数 > pending.days（与后端 overdue 同一阈值口径）
  const isOverdueFlagged = (h: HandoverRecord): boolean =>
    !!pending && pending.pending > 0 && h.review?.verdict === 'flagged' &&
    Date.now() - new Date(h.review.createdAt).getTime() > pending.days * 86400000;

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);
  useEffect(() => { if (isAdmin) fetchHandovers(0, false); }, [isAdmin, fetchHandovers]);
  useEffect(() => { if (isAdmin) fetchPending(); }, [isAdmin, fetchPending]);
  useEffect(() => { if (isAdmin) fetchMetrics(); }, [isAdmin, fetchMetrics]);

  if (!isAdmin) {
    return (
      <div className="card card-pad text-sm text-slate-600">
        🔒 成员管理仅对<b>管理员</b>开放。如需调整团队或数据归属，请联系管理员。
      </div>
    );
  }

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');
    if (!form.username.trim() || form.password.length < 8) {
      setError('用户名必填，密码至少 8 位');
      return;
    }
    setSaving(true);
    try {
      await usersApi.create({
        username: form.username.trim(),
        password: form.password,
        displayName: form.displayName.trim() || undefined,
        role: form.role,
      });
      setNotice(`已创建成员「${form.displayName.trim() || form.username.trim()}」`);
      setForm({ ...emptyForm });
      setShowForm(false);
      await load();
    } catch (err: any) {
      setError(err.message || '创建失败');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u: UserBrief) => {
    const next = !u.isActive;
    if (!window.confirm(`${next ? '启用' : '停用'}成员「${u.displayName}（${u.username}）」？${next ? '' : '\n停用后该成员将无法登录；若其名下有负责档案，接下来需选择交接对象。'}`)) return;
    setError('');
    setNotice('');
    try {
      await usersApi.setActive(u.id, next);
      setNotice(`已${next ? '启用' : '停用'}「${u.displayName}」`);
      await load();
    } catch (err: any) {
      // 8.4.3：名下有档案未交接 → 409，弹交接人选择框
      if (err.status === 409 && err.code === 'HAS_OWNED_PARTIES') {
        setTransferTo('');
        setHandover({ user: u, count: err.data?.count ?? 0 });
        return;
      }
      setError(err.message || '操作失败');
    }
  };

  const confirmHandover = async () => {
    if (!handover || !transferTo) return;
    setHandoverSaving(true);
    setError('');
    try {
      const r = await usersApi.setActive(handover.user.id, false, transferTo);
      const to = transferTo === 'public' ? '公开' : (users.find((x) => x.id === transferTo)?.displayName || '指定成员');
      setNotice(`已停用「${handover.user.displayName}」，名下 ${r.transferred ?? handover.count} 份档案已交接给${to}`);
      setHandover(null);
      await load();
      await fetchHandovers(0, false); // 8.4.4 交接后刷新审计轨迹（回到首页）
    } catch (err: any) {
      setError(err.message || '交接失败');
    } finally {
      setHandoverSaving(false);
    }
  };

  // 8.4.8 打开离职向导：先拉预览（名下档案+未结/超期），失败则不阻断（降级为仅提示）
  const openWizard = async (u: UserBrief) => {
    setError('');
    setWizTo('');
    setWizDetail(null);
    setWizResolvingId(null);
    setWizResolveNote('');
    setWizLoading(true);
    setWizard({ user: u, preview: null });
    try {
      const p = await usersApi.handoverPreview(u.id);
      setWizard({ user: u, preview: p });
    } catch (e: any) {
      setError(e?.message || '加载交接预览失败，可直接在列表行使“停用”流程');
      setWizard(null);
    } finally {
      setWizLoading(false);
    }
  };

  // 8.4.14 展开/收起遗留疑点明细：首次展开懒加载，再点收起
  const toggleWizDetail = async () => {
    if (!wizard) return;
    if (wizDetail) { setWizDetail(null); return; }
    setWizDetailLoading(true);
    try {
      const { items } = await usersApi.pendingReviewsDetail(wizard.user.id);
      setWizDetail(items);
    } catch {
      setWizDetail([]);
    } finally {
      setWizDetailLoading(false);
    }
  };

  // 8.4.8 确认离职：有档案必选交接对象（复用 8.4.3 服务端 reassign+逐份留痕），无档案直接停用
  const confirmWizard = async () => {
    if (!wizard) return;
    const preview = wizard.preview;
    const hasOwned = !!preview && preview.ownedCount > 0;
    if (hasOwned && !wizTo) return;
    setWizSaving(true);
    setError('');
    try {
      const r = await usersApi.setActive(wizard.user.id, false, hasOwned ? wizTo : undefined);
      const to = wizTo === 'public' ? '公开' : (users.find((x) => x.id === wizTo)?.displayName || '指定成员');
      setNotice(hasOwned
        ? `已办理「${wizard.user.displayName}」离职：名下 ${r.transferred ?? preview?.ownedCount} 份档案已交接给${to}，成员已停用`
        : `已停用「${wizard.user.displayName}」（名下无负责档案）`);
      setWizard(null);
      await load();
      await fetchHandovers(0, false);
    } catch (err: any) {
      setError(err.message || '离职交接失败');
    } finally {
      setWizSaving(false);
    }
  };

  // 8.4.9 打开复核批注弹框（预填已有最新结论）
  const openReview = (h: HandoverRecord) => {
    setError('');
    setReviewVerdict(h.review?.verdict || 'confirmed');
    setReviewNote(h.review?.note || '');
    setReviewing(h);
  };

  // 8.4.9 提交复核：向审计记录追加一条只增不改的批注，就地刷新（保持当前分页/筛选）
  const submitReview = async () => {
    if (!reviewing) return;
    setReviewSaving(true);
    setError('');
    try {
      await usersApi.addHandoverReview(reviewing.id, reviewVerdict, reviewNote.trim());
      setNotice(`已对「${reviewing.partyName}」的交接记录追加复核批注（${REVIEW_LABELS[reviewVerdict]?.text || reviewVerdict}）`);
      setReviewing(null);
      await fetchHandovers(0, false); // 刷新首页以展示最新复核关联
      void fetchPending(); // 批注可能改变待处理/超期计数
    } catch (err: any) {
      setError(err.message || '复核失败');
    } finally {
      setReviewSaving(false);
    }
  };

  // 8.4.10 打开复核轨迹弹层：懒加载该交接记录的历次批注全量（时间倒序）
  const openTrail = async (h: HandoverRecord) => {
    setError('');
    setTrail({ handover: h, reviews: [] });
    setTrailLoading(true);
    try {
      const reviews = await usersApi.listHandoverReviews(h.id);
      setTrail({ handover: h, reviews });
    } catch (e: any) {
      setError(e?.message || '加载复核轨迹失败');
      setTrail(null);
    } finally {
      setTrailLoading(false);
    }
  };

  // 8.4.11 销案：疑点核实后追加【销案】confirmed 批注（仍只增不改），成功后就地刷新
  const submitResolve = async () => {
    if (!resolving) return;
    setResolveSaving(true);
    setError('');
    try {
      await usersApi.resolveHandover(resolving.id, resolveNote.trim());
      setNotice(`已对「${resolving.partyName}」的可疑交接记录销案（追加确认批注，疑点历史保留）`);
      setResolving(null);
      await fetchHandovers(0, false);
      void fetchPending(); // 销案后待处理/超期计数即时下降
    } catch (err: any) {
      setError(err.message || '销案失败');
    } finally {
      setResolveSaving(false);
    }
  };

  // 8.4.15 行内直接销案：下钻清单不跳审计列表即逐条处置（仍走只增不改的 resolveHandover）；
  // 成功后本地剔除该行 + 刷新 preview.pendingReviews（预警条即时收敛）+ 徽标待处理数
  const resolveInline = async (handoverId: string, note: string) => {
    if (!wizard || !wizard.preview) return;
    setWizResolveSaving(true);
    setError('');
    try {
      await usersApi.resolveHandover(handoverId, note.trim());
      setNotice(note.trim() ? '已就地销案一条遗留疑点（含处置说明，追加【销案】确认批注）' : '已就地销案一条遗留疑点（追加【销案】确认批注，疑点历史保留）');
      // 8.4.17 就地销案后自动收起：过滤掉该行，若清单尚余行保持展开、若已销完则置 null（收起）避免「无明细」空态闪现
      setWizDetail((prev) => {
        if (!prev) return prev;
        const rest = prev.filter((x) => x.handoverId !== handoverId);
        return rest.length ? rest : null;
      });
      setWizResolvingId(null);
      setWizResolveNote('');
      const fresh = await usersApi.handoverPreview(wizard.user.id, wizard.preview.pendingReviews?.hours);
      setWizard({ user: wizard.user, preview: fresh });
      void fetchPending(); // 徽标/横幅待处理数同口径下降
    } catch (err: any) {
      setError(err.message || '销案失败');
    } finally {
      setWizResolveSaving(false);
    }
  };

  const changeRole = async (u: UserBrief, role: string) => {
    if (role === u.role) return;
    if (!window.confirm(`将「${u.displayName}」的角色由「${ROLE_LABELS[u.role]}」变更为「${ROLE_LABELS[role] || role}」？\n新角色在该成员下次登录后生效。`)) return;
    setError('');
    setNotice('');
    try {
      await usersApi.setRole(u.id, role);
      setNotice(`已将「${u.displayName}」设为${ROLE_LABELS[role] || role}`);
      await load();
    } catch (err: any) {
      setError(err.message || '角色变更失败');
      await load();
    }
  };

  const resetPwd = async (u: UserBrief) => {
    const pwd = window.prompt(`为「${u.displayName}（${u.username}）」设置新密码（至少 8 位，下次登录生效）：`);
    if (pwd === null) return;
    if (pwd.length < 8) { setError('密码至少 8 位'); return; }
    setError('');
    setNotice('');
    try {
      await usersApi.resetPassword(u.id, pwd);
      setNotice(`已重置「${u.displayName}」的密码，请通知其使用新密码登录`);
    } catch (err: any) {
      setError(err.message || '重置失败');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-800">团队成员</h2>
          <p className="text-xs text-slate-500 mt-0.5">共 {users.length} 名成员（{users.filter((u) => u.isActive).length} 人启用）· 数据归属与可见范围（8.4）负责人来源</p>
        </div>
        <button onClick={() => setShowForm((v) => !v)} className="btn btn-primary btn-sm">
          {showForm ? '取消' : '+ 新建成员'}
        </button>
      </div>

      {notice && <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{notice}</div>}
      {error && <ErrorBanner message={error} />}

      {showForm && (
        <form onSubmit={submit} className="card card-pad grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">用户名 *</label>
            <input value={form.username} onChange={(e) => set({ username: e.target.value })} placeholder="3-30 位字母/数字/._-" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" maxLength={30} />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">初始密码 *</label>
            <input type="password" value={form.password} onChange={(e) => set({ password: e.target.value })} placeholder="至少 8 位" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">显示名</label>
            <input value={form.displayName} onChange={(e) => set({ displayName: e.target.value })} placeholder="缺省同用户名" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" maxLength={30} />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">角色</label>
            <select value={form.role} onChange={(e) => set({ role: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="accountant">会计（仅自己客户）</option>
              <option value="senior_accountant">主管会计（全量+可圈定）</option>
              <option value="admin">管理员（全量+成员管理）</option>
              <option value="viewer">浏览员（只读）</option>
            </select>
          </div>
          <div className="col-span-2 md:col-span-4">
            <button type="submit" disabled={saving} className="btn btn-primary btn-sm">{saving ? '创建中…' : '创建成员'}</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="px-4 py-2.5">成员</th>
              <th className="px-4 py-2.5">用户名</th>
              <th className="px-4 py-2.5">角色</th>
              <th className="px-4 py-2.5">状态</th>
              <th className="px-4 py-2.5">最近登录</th>
              <th className="px-4 py-2.5 text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows colSpan={6} />
            ) : users.length === 0 ? (
              <TableEmpty colSpan={6} icon="👥" title="还没有成员" hint="点击右上角「新建成员」添加团队" />
            ) : (
              users.map((u) => (
                <tr key={u.id} className={`border-t border-slate-100 ${u.isActive ? '' : 'opacity-55'}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">
                    {u.displayName}
                    {u.id === me?.id && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-brand-50 text-brand-700">我</span>}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 font-mono text-xs">{u.username}</td>
                  <td className="px-4 py-2.5">
                    {u.id === me?.id ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{ROLE_LABELS[u.role] || u.role}</span>
                    ) : (
                      <select
                        value={u.role}
                        onChange={(e) => changeRole(u, e.target.value)}
                        title="变更角色（仅 admin；自己不可变更）"
                        className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-700 hover:border-slate-300"
                      >
                        <option value="admin">管理员</option>
                        <option value="senior_accountant">主管会计</option>
                        <option value="accountant">会计</option>
                        <option value="viewer">浏览员</option>
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {u.isActive
                      ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">启用</span>
                      : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">已停用</span>}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">{u.lastLogin ? String(u.lastLogin).slice(0, 16).replace('T', ' ') : '—'}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button
                      onClick={() => openWizard(u)}
                      disabled={!u.isActive || u.id === me?.id}
                      title={!u.isActive ? '已停用无需交接' : (u.id === me?.id ? '不能对自己发起离职交接' : '预览名下档案并一键交接后停用')}
                      className="text-xs text-indigo-500 hover:underline mr-3 disabled:text-slate-300 disabled:cursor-not-allowed"
                    >
                      离职交接
                    </button>
                    <button
                      onClick={() => resetPwd(u)}
                      title="重置密码（下次登录生效）"
                      className="text-xs text-slate-500 hover:underline mr-3"
                    >
                      重置密码
                    </button>
                    <button
                      onClick={() => toggleActive(u)}
                      disabled={u.id === me?.id}
                      title={u.id === me?.id ? '不能停用当前登录的自己' : (u.isActive ? '停用该成员（名下档案需交接）' : '启用该成员')}
                      className={`text-xs hover:underline disabled:text-slate-300 disabled:cursor-not-allowed ${u.isActive ? 'text-red-500' : 'text-emerald-600'}`}
                    >
                      {u.isActive ? '停用' : '启用'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 8.4.4 交接审计轨迹：成员离职/交接合规留痕（谁在何时把哪份档案从谁转给谁） */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between flex-wrap gap-3 px-4 py-3 border-b border-slate-100">
          <div>
            <h3 className="text-sm font-bold text-slate-800">📋 交接审计轨迹</h3>
            <p className="text-xs text-slate-500 mt-0.5">停用交接 / 直接重指派均逐份留痕，仅增不改；可按成员/时间范围/复核状态筛选、导出 CSV，对单条记录追加复核批注、回看历次复核全史，可疑待核可销案闭环</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={handoverFilter}
              onChange={(e) => setHandoverFilter(e.target.value)}
              title="按成员过滤（该成员交出或接入）"
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700"
            >
              <option value="">全部成员</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.displayName}（{u.username}）</option>
              ))}
            </select>
            <select
              value={reviewFilter}
              onChange={(e) => setReviewFilter(e.target.value)}
              title="按最新复核结论筛选（8.4.11）"
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700"
            >
              <option value="">全部复核状态</option>
              <option value="none">未复核</option>
              <option value="flagged">可疑待核（待处理）</option>
              <option value="confirmed">已确认</option>
              <option value="corrected">已更正</option>
            </select>
            <input type="date" value={handoverFrom} max={handoverTo || undefined} onChange={(e) => setHandoverFrom(e.target.value)} title="起始日期" className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700" />
            <span className="text-xs text-slate-400">至</span>
            <input type="date" value={handoverTo} min={handoverFrom || undefined} onChange={(e) => setHandoverTo(e.target.value)} title="截至日期" className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700" />
            <button onClick={doExport} disabled={exporting} title="按当前筛选导出 CSV" className="btn btn-sm bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed">{exporting ? '导出中…' : '⬇ 导出 CSV'}</button>
          </div>
        </div>
        {/* 8.4.12 待处理预警横幅：存在最新结论为可疑待核的记录时主动催办，一键切入 flagged 筛选 */}
        {(pending?.pending ?? 0) > 0 && (
          <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-2.5 bg-amber-50 border-b border-amber-100">
            <p className="text-xs text-amber-800">
              ⚠️ 可疑待核未销案 <b>{pending!.pending}</b> 条{pending!.overdue > 0 ? <>，其中超 <b>{pending!.days}</b> 天未销案 <b>{pending!.overdue}</b> 条</> : '，请及时处理'}
            </p>
            <button
              onClick={() => setReviewFilter('flagged')}
              className="btn btn-sm bg-amber-600 text-white hover:bg-amber-700"
            >
              只看待处理
            </button>
          </div>
        )}
        {/* 8.4.18 复核时效轻报表（按 flagged 发起人聚合） */}
        {metrics && metrics.byReviewer.length > 0 && (
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs font-semibold text-slate-600">⏱ 复核时效</span>
              <span className="text-[11px] text-slate-400">超期阈值 {metrics.thresholdHours}h · 待处理 {metrics.pendingTotal}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="text-[11px] w-auto">
                <thead className="text-slate-500">
                  <tr><th className="pr-4 text-left">复核人</th><th className="pr-3 text-right">标记</th><th className="pr-3 text-right">已销</th><th className="pr-3 text-right">均时(h)</th><th className="pr-3 text-right">超期</th><th className="text-right">待处理</th></tr>
                </thead>
                <tbody className="text-slate-700">
                  {metrics.byReviewer.map((r) => (
                    <tr
                      key={r.operatorId}
                      onClick={r.stillPending > 0 ? () => { setReviewFilter('flagged'); fetchHandovers(0, false); } : undefined}
                      className={r.stillPending > 0 ? 'cursor-pointer hover:bg-slate-200/60 transition-colors' : ''}
                      title={r.stillPending > 0 ? '点击只看该复核人标记的可疑待核' : undefined}
                    >
                      <td className="pr-4">{r.reviewerName}（{r.reviewerUsername}）</td>
                      <td className="pr-3 text-right">{r.totalFlagged}</td>
                      <td className="pr-3 text-right">{r.resolvedCount}</td>
                      <td className="pr-3 text-right">{r.avgHours != null ? r.avgHours.toFixed(1) : '—'}</td>
                      <td className={`pr-3 text-right ${r.overdueResolved > 0 ? 'text-red-600 font-semibold' : ''}`}>{r.overdueResolved}</td>
                      <td className="text-right">{r.stillPending}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="px-4 py-2.5">时间</th>
              <th className="px-4 py-2.5">档案</th>
              <th className="px-4 py-2.5">移交</th>
              <th className="px-4 py-2.5">执行人</th>
              <th className="px-4 py-2.5">复核</th>
            </tr>
          </thead>
          <tbody>
            {handoversLoading ? (
              <SkeletonRows colSpan={5} />
            ) : handovers.length === 0 ? (
              <TableEmpty colSpan={5} icon="🗂️" title="暂无交接记录" hint="停用名下有档案的成员、或直接改档案负责人后，此处会留痕" />
            ) : (
              handovers.map((h) => (
                <tr key={h.id} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 text-slate-500 text-xs whitespace-nowrap">{String(h.createdAt).slice(0, 16).replace('T', ' ')}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-700">
                    {h.partyName}
                    {(() => { const rl = REASON_LABELS[h.reason] || REASON_LABELS.deactivation; return <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded ${rl.cls}`}>{rl.text}</span>; })()}
                  </td>
                  <td className="px-4 py-2.5 text-xs">
                    <span className="text-slate-500">{h.fromName || '—'}</span>
                    <span className="mx-1.5 text-slate-400">→</span>
                    <span className={h.toUserId ? 'text-slate-700' : 'text-slate-400'}>{h.toName || '公开池'}</span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-500">{h.operatorName || '—'}</td>
                  <td className="px-4 py-2.5 text-xs">
                    {h.review ? (
                      <span className="inline-flex items-center gap-1.5" title={h.review.note ? `${h.review.operatorName || '复核人'}：${h.review.note}` : (h.review.operatorName || '复核人')}>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${(REVIEW_LABELS[h.review.verdict] || REVIEW_LABELS.confirmed).cls}`}>{(REVIEW_LABELS[h.review.verdict] || REVIEW_LABELS.confirmed).text}</span>
                        {h.review.note && <span className="text-slate-500 truncate max-w-[12rem]">{h.review.note}</span>}
                        {isOverdueFlagged(h) && (
                          <span title={`距最新可疑批注已超 ${pending?.days ?? 7} 天，请尽快核实销案`} className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-600 font-semibold">超期未销案</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-slate-400">未复核</span>
                    )}
                    <button onClick={() => openReview(h)} title="追加复核批注（只增不改）" className="ml-1.5 text-brand-600 hover:underline">{h.review ? '改批注' : '复核'}</button>
                    {h.review?.verdict === 'flagged' && (
                      <button onClick={() => { setError(''); setResolveNote(''); setResolving(h); }} title="疑点已核实：追加【销案】确认批注" className="ml-1.5 text-emerald-600 hover:underline">销案</button>
                    )}
                    {(h.reviewCount || 0) > 0 && (
                      <button onClick={() => openTrail(h)} title={`查看历次复核全史（共 ${h.reviewCount} 次）`} className="ml-1.5 text-slate-400 hover:text-brand-600 hover:underline">轨迹×{h.reviewCount}</button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {handovers.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 text-xs text-slate-500">
            <span>已加载 {handovers.length} 条{handoverFilter || handoverFrom || handoverTo ? '（按当前筛选）' : ''}</span>
            {handoverHasMore ? (
              <button onClick={() => fetchHandovers(handoverOffset, true)} disabled={handoversLoading} className="text-brand-600 hover:underline disabled:text-slate-400">{handoversLoading ? '加载中…' : '加载更多 ⌄'}</button>
            ) : (
              <span className="text-slate-400">无更多</span>
            )}
          </div>
        )}
      </div>

      {/* 8.4.3 停用交接弹窗：选定转入成员或转公开后重试停用 */}
      {handover && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4" onClick={() => !handoverSaving && setHandover(null)}>
          <div className="bg-white rounded-xl shadow-lg p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-slate-800 mb-2">⚠️ 停用前需交接档案</h3>
            <p className="text-xs text-slate-600 leading-relaxed mb-3">
              成员「{handover.user.displayName}（{handover.user.username}）」名下尚有 <b>{handover.count}</b> 份启用档案负责。
              若不停用直接交接，这些档案将变成「本人无法登录、他人又被屏蔽」的孤儿档案。请选择交接方式：
            </p>
            <select value={transferTo} onChange={(e) => setTransferTo(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white mb-4">
              <option value="">请选择交接对象…</option>
              <option value="public">转为公开（无主，全员可见可录入，由 admin/senior 后续再指派）</option>
              {users.filter((x) => x.isActive && x.id !== handover.user.id).map((x) => (
                <option key={x.id} value={x.id}>移交成员：{x.displayName}（{ROLE_LABELS[x.role] || x.role}）</option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button onClick={() => setHandover(null)} disabled={handoverSaving} className="btn btn-sm bg-white border border-slate-200 text-slate-700 hover:bg-slate-100">取消（不停用）</button>
              <button onClick={confirmHandover} disabled={!transferTo || handoverSaving} className="btn btn-primary btn-sm">{handoverSaving ? '处理中…' : '交接并停用'}</button>
            </div>
          </div>
        </div>
      )}

      {/* 8.4.8 离职交接向导：交接前聚合名下档案及未结/超期，选接管人后一键交接+停用 */}
      {wizard && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4" onClick={() => !wizSaving && setWizard(null)}>
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-800">🧭 离职交接向导</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                为「{wizard.user.displayName}（{wizard.user.username}·{ROLE_LABELS[wizard.user.role] || wizard.user.role}）」办理离职：先确认名下档案、未结核算与经手交接的遗留待核预警，再选定接管人，交接与停用一次完成并逐份留痕。
              </p>
            </div>
            <div className="px-5 py-4 overflow-y-auto flex-1">
              {wizLoading || !wizard.preview ? (
                <div className="text-sm text-slate-400 py-8 text-center">{wizLoading ? '正在汇总名下档案与账龄…' : '预览加载失败，请关闭后改用“停用”流程'}</div>
              ) : wizard.preview.ownedCount === 0 ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-700">
                  ✅ 该成员名下无负责档案，无需交接，可直接停用。
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                    <div className="rounded-lg bg-slate-50 py-2"><div className="text-lg font-bold text-slate-800">{wizard.preview.ownedCount}</div><div className="text-[11px] text-slate-500">名下档案</div></div>
                    <div className="rounded-lg bg-slate-50 py-2"><div className="text-lg font-bold text-slate-800">¥{money(wizard.preview.totals.openAmount)}</div><div className="text-[11px] text-slate-500">未结合计（{wizard.preview.totals.openCount} 笔）</div></div>
                    <div className="rounded-lg bg-amber-50 py-2"><div className="text-lg font-bold text-amber-700">¥{money(wizard.preview.totals.overdueAmount)}</div><div className="text-[11px] text-slate-500">其中超期（{wizard.preview.totals.overdueCount} 笔）</div></div>
                  </div>
                  <table className="w-full text-xs">
                    <thead className="text-slate-500 text-left">
                      <tr>
                        <th className="py-1.5 pr-2">档案</th>
                        <th className="py-1.5 pr-2">类型</th>
                        <th className="py-1.5 pr-2 text-right">未结金额</th>
                        <th className="py-1.5 pr-2 text-right">笔数</th>
                        <th className="py-1.5 text-right">超期金额</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wizard.preview.ownedParties.map((p) => (
                        <tr key={p.id} className="border-t border-slate-100">
                          <td className="py-1.5 pr-2 font-medium text-slate-700">{p.name}</td>
                          <td className="py-1.5 pr-2 text-slate-500">{PARTY_TYPE_LABELS[p.type] || p.type}</td>
                          <td className="py-1.5 pr-2 text-right tabular-nums text-slate-700">¥{money(p.openAmount)}</td>
                          <td className="py-1.5 pr-2 text-right tabular-nums text-slate-500">{p.openCount}</td>
                          <td className={`py-1.5 text-right tabular-nums ${p.overdueAmount > 0 ? 'text-amber-600 font-medium' : 'text-slate-400'}`}>{p.overdueAmount > 0 ? `¥${money(p.overdueAmount)}` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-[11px] text-slate-400 mt-2">未结/超期按账龄口径（应收+应付两侧）按档案归户汇总，仅供交接参考；交接后这些档案的负责人将变更。</p>
                </>
              )}
              {/* 8.4.13 未销案预警 + 8.4.14 遗留疑点下钻：经手交接中最新结论 flagged 者（与档案清单独立，零档案也提示），防「人走案悬」 */}
              {wizard.preview && (wizard.preview.pendingReviews?.pending ?? 0) > 0 && (() => {
                const pr = wizard.preview!.pendingReviews!;
                return (
                  <div className={`mt-3 rounded-lg border px-3 py-2.5 text-xs ${pr.overdue > 0 ? 'border-red-200 bg-red-50 text-red-700' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                    <div>⚠️ 该成员经手的交接记录中有 <b>{pr.pending}</b> 条可疑待核尚未销案{pr.overdue > 0 ? <>，其中 <b>{pr.overdue}</b> 条距最新可疑批注已超 <b>{pr.hours}</b> 小时</> : '（均未超时限）'}。建议先完成处置再办理离职，避免人走案悬。</div>
                    <div className="mt-2 flex flex-wrap gap-3">
                      <button onClick={toggleWizDetail} className="underline font-medium">{wizDetail ? '收起遗留疑点清单' : `查看 ${pr.pending} 条遗留疑点`}</button>
                      <button onClick={() => { setReviewFilter('flagged'); setWizard(null); }} className="underline font-medium">前往审计列表处置</button>
                    </div>
                    {wizDetail && (
                      <div className="mt-2 space-y-1.5">
                        {wizDetailLoading && <div className="text-slate-500">加载明细中…</div>}
                        {!wizDetailLoading && wizDetail.length === 0 && <div className="text-slate-500">无明细（可能已就地处置）</div>}
                        {!wizDetailLoading && wizDetail.map((it) => (
                          <div key={it.handoverId} className="rounded bg-white/70 border border-black/5 px-2 py-1.5">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-700">{it.partyName}</span>
                              <span className="text-[10px] px-1 rounded bg-slate-200/70 text-slate-600">{it.direction === 'out' ? '交出' : '接入'}</span>
                              {it.isOverdue && <span className="text-[10px] px-1 rounded bg-red-100 text-red-600">超期未销</span>}
                            </div>
                            <div className="text-slate-600 mt-0.5">疑点：{it.note || '（无批注）'}</div>
                            <div className="text-[10px] text-slate-400 mt-0.5">复核：{it.reviewerName || '—'} · {String(it.reviewCreatedAt).slice(0, 16).replace('T', ' ')} · 已挂 {it.ageHours} 小时{it.isOverdue ? '' : `（阈值 ${pr.hours}h 内）`}</div>
                            {/* 8.4.15 行内直接销案 + 8.4.16 可选附注：确认态展示轻量说明输入（留空走默认回退文案），防误点 */}
                            {wizResolvingId === it.handoverId ? (
                              <div className="mt-1.5">
                                <input
                                  type="text"
                                  value={wizResolveNote}
                                  onChange={(e) => setWizResolveNote(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') resolveInline(it.handoverId, wizResolveNote); }}
                                  maxLength={200}
                                  disabled={wizResolveSaving}
                                  placeholder="处置说明（可选，留空记为「疑点已核实，确认无异议」）"
                                  className="w-full rounded border border-black/10 px-2 py-1 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-green-500"
                                />
                                <div className="mt-1 flex justify-end gap-2">
                                  <button className="text-[10px] text-slate-400 disabled:opacity-50" disabled={wizResolveSaving} onClick={() => { setWizResolvingId(null); setWizResolveNote(''); }}>取消</button>
                                  <button className="text-[10px] px-1.5 py-0.5 rounded bg-green-600 text-white disabled:bg-green-600/50" disabled={wizResolveSaving} onClick={() => resolveInline(it.handoverId, wizResolveNote)}>{wizResolveSaving ? '销案中…' : '确认销案'}</button>
                                </div>
                              </div>
                            ) : (
                              <div className="mt-1 flex justify-end gap-2">
                                <button className="text-[10px] px-1.5 py-0.5 rounded border border-green-600/40 text-green-700 hover:bg-green-50" disabled={wizResolveSaving} onClick={() => { setWizResolveNote(''); setWizResolvingId(it.handoverId); }}>销案</button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            {wizard.preview && wizard.preview.ownedCount > 0 && (
              <div className="px-5 py-3 border-t border-slate-100">
                <label className="block text-xs text-slate-500 mb-1">交接对象 *</label>
                <select value={wizTo} onChange={(e) => setWizTo(e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="">请选择接管人…</option>
                  <option value="public">转为公开（无主，全员可见可录入，由 admin/senior 后续再指派）</option>
                  {users.filter((x) => x.isActive && x.id !== wizard.user.id).map((x) => (
                    <option key={x.id} value={x.id}>移交成员：{x.displayName}（{ROLE_LABELS[x.role] || x.role}）</option>
                  ))}
                </select>
              </div>
            )}
            <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
              <button onClick={() => setWizard(null)} disabled={wizSaving} className="btn btn-sm bg-white border border-slate-200 text-slate-700 hover:bg-slate-100">取消</button>
              <button
                onClick={confirmWizard}
                disabled={wizSaving || (!!wizard.preview && wizard.preview.ownedCount > 0 && !wizTo)}
                className="btn btn-primary btn-sm"
              >
                {wizSaving ? '处理中…' : (wizard.preview && wizard.preview.ownedCount > 0 ? '确认交接并停用' : '确认停用')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 8.4.9 复核批注弹框：向一条交接记录追加合规结论（只增不改，不改历史） */}
      {reviewing && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4" onClick={() => !reviewSaving && setReviewing(null)}>
          <div className="bg-white rounded-xl shadow-lg p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-slate-800 mb-1">📝 交接复核批注</h3>
            <p className="text-xs text-slate-500 mb-3">
              对「{reviewing.partyName}」于 {String(reviewing.createdAt).slice(0, 16).replace('T', ' ')} 的交接记录（{(REASON_LABELS[reviewing.reason] || REASON_LABELS.deactivation).text}）追加复核结论。批注只增不改，不会抹除原始留痕。
            </p>
            <div className="flex gap-2 mb-3">
              {(['confirmed', 'corrected', 'flagged'] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setReviewVerdict(v)}
                  className={`flex-1 text-xs px-2 py-1.5 rounded-lg border ${reviewVerdict === v ? 'border-brand-500 ring-1 ring-brand-200 ' + REVIEW_LABELS[v].cls : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                >
                  {REVIEW_LABELS[v].text}
                </button>
              ))}
            </div>
            <textarea value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} rows={3} placeholder="复核说明（可选）：如已核对凭证、需更正原因、可疑点等" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm resize-none" maxLength={200} />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setReviewing(null)} disabled={reviewSaving} className="btn btn-sm bg-white border border-slate-200 text-slate-700 hover:bg-slate-100">取消</button>
              <button onClick={submitReview} disabled={reviewSaving} className="btn btn-primary btn-sm">{reviewSaving ? '提交中…' : '提交批注'}</button>
            </div>
          </div>
        </div>
      )}

      {/* 8.4.10 复核轨迹弹层：一条交接记录的历次批注全史（时间倒序，只增不改不抹除） */}
      {trail && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4" onClick={() => setTrail(null)}>
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-800">🕮 复核轨迹全史</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                「{trail.handover.partyName}」于 {String(trail.handover.createdAt).slice(0, 16).replace('T', ' ')} 的交接记录，共 {trail.handover.reviewCount} 次复核（最新在前；历次批注全量保留）。
              </p>
            </div>
            <div className="px-5 py-4 overflow-y-auto flex-1">
              {trailLoading ? (
                <div className="text-sm text-slate-400 py-6 text-center">正在加载复核轨迹…</div>
              ) : trail.reviews.length === 0 ? (
                <div className="text-sm text-slate-400 py-6 text-center">暂无复核批注</div>
              ) : (
                <ul className="space-y-2.5">
                  {trail.reviews.map((rv, i) => {
                    const rl = REVIEW_LABELS[rv.verdict] || REVIEW_LABELS.confirmed;
                    return (
                      <li key={rv.id} className="rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2">
                        <div className="flex items-center gap-2 flex-wrap text-xs">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${rl.cls}`}>{rl.text}</span>
                          {i === 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-50 text-brand-700">最新</span>}
                          <span className="text-slate-400">{String(rv.createdAt).slice(0, 19).replace('T', ' ')}</span>
                          <span className="text-slate-500">· {rv.operatorName || '复核人'}</span>
                        </div>
                        {rv.note && <p className="mt-1 text-xs text-slate-700 leading-relaxed break-words">{rv.note}</p>}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
              <button onClick={() => setTrail(null)} className="btn btn-sm bg-white border border-slate-200 text-slate-700 hover:bg-slate-100">关闭</button>
              <button
                onClick={() => { const h = trail.handover; setTrail(null); openReview(h); }}
                className="btn btn-primary btn-sm"
              >
                追加新批注
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 8.4.11 销案弹框：对可疑待核记录追加【销案】确认批注（疑点历史保留） */}
      {resolving && (
        <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4" onClick={() => !resolveSaving && setResolving(null)}>
          <div className="bg-white rounded-xl shadow-lg p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-slate-800 mb-1">✅ 疑点销案</h3>
            <p className="text-xs text-slate-500 mb-3">
              对「{resolving.partyName}」于 {String(resolving.createdAt).slice(0, 16).replace('T', ' ')} 的交接记录（最新复核：可疑待核 · {resolving.review?.note || '无批注'}）确认疑点已核实。销案将追加一条【销案】确认批注，疑点历史仍完整保留。
            </p>
            <textarea value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} rows={3} placeholder="销案说明（可选）：如已补充凭证、已当面确认等" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm resize-none" maxLength={200} />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setResolving(null)} disabled={resolveSaving} className="btn btn-sm bg-white border border-slate-200 text-slate-700 hover:bg-slate-100">取消（保留待核）</button>
              <button onClick={submitResolve} disabled={resolveSaving} className="btn btn-primary btn-sm">{resolveSaving ? '处理中…' : '确认销案'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
